"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

type EncryptedTextProps = {
  text: string;
  className?: string;
  revealDelayMs?: number;
  charset?: string;
  flipDelayMs?: number;
  encryptedClassName?: string;
  revealedClassName?: string;
};

const DEFAULT_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-={}[];:,.<>/?";

function generateRandomCharacter(charset: string): string {
  const index = Math.floor(Math.random() * charset.length);
  return charset.charAt(index);
}

function generateGibberishPreservingSpaces(original: string, charset: string) {
  if (!original) return "";
  let result = "";
  for (let i = 0; i < original.length; i += 1) {
    const ch = original[i];
    result += ch === " " ? " " : generateRandomCharacter(charset);
  }
  return result;
}

export const EncryptedText: React.FC<EncryptedTextProps> = ({
  text,
  className,
  revealDelayMs = 50,
  charset = DEFAULT_CHARSET,
  flipDelayMs = 50,
  encryptedClassName = "",
  revealedClassName = "",
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  // mounted ensures we don't change initial server/client markup
  const [mounted, setMounted] = useState(false);
  const [revealCount, setRevealCount] = useState<number>(0);

  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastFlipTimeRef = useRef<number>(0);
  const scrambleCharsRef = useRef<string[] | null>(null);

  // Ensure initial render returns deterministic markup matching server:
  // we render the real characters (not randomized) until mounted=true.
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !isInView) return;

    // initialize scramble chars on client only (uses random)
    const initial = text
      ? generateGibberishPreservingSpaces(text, charset)
      : "";
    scrambleCharsRef.current = initial.split("");

    startTimeRef.current = performance.now();
    lastFlipTimeRef.current = startTimeRef.current;
    setRevealCount(0);

    let isCancelled = false;

    const update = (now: number) => {
      if (isCancelled) return;

      const elapsedMs = now - startTimeRef.current;
      const totalLength = text.length;
      const currentRevealCount = Math.min(
        totalLength,
        Math.floor(elapsedMs / Math.max(1, revealDelayMs)),
      );

      // update reveal count (causes re-render)
      setRevealCount(currentRevealCount);

      if (currentRevealCount >= totalLength) {
        return;
      }

      const timeSinceLastFlip = now - lastFlipTimeRef.current;
      if (timeSinceLastFlip >= Math.max(0, flipDelayMs)) {
        for (let index = 0; index < totalLength; index += 1) {
          if (index >= currentRevealCount) {
            if (text[index] !== " ") {
              scrambleCharsRef.current![index] =
                generateRandomCharacter(charset);
            } else {
              scrambleCharsRef.current![index] = " ";
            }
          }
        }
        lastFlipTimeRef.current = now;
      }

      animationFrameRef.current = requestAnimationFrame(update);
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      isCancelled = true;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mounted, isInView, text, revealDelayMs, charset, flipDelayMs]);

  if (!text) return null;

  // On server and initial client render (mounted === false) we output the real text,
  // so server and client markup match. After mount we start the scramble animation.
  return (
    <motion.span
      ref={ref}
      className={`${className ?? ""}`}
      aria-label={text}
      role="text"
    >
      {text.split("").map((char, index) => {
        const isRevealed = mounted ? index < revealCount : true; // show revealed on initial render so SSR matches
        const displayChar = mounted
          ? isRevealed
            ? char
            : char === " "
              ? " "
              : (scrambleCharsRef.current?.[index] ?? char)
          : char; // before mounted, show the real char (deterministic)

        return (
          <span
            key={index}
            className={`${isRevealed ? revealedClassName : encryptedClassName}`}
          >
            {displayChar}
          </span>
        );
      })}
    </motion.span>
  );
};
